import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, SearchableSelect } from './ui';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/payments';
const PAYMENT_TYPE_API = '/api/paymenttypes';
const INVOICE_API = '/api/invoices';
const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 0, label: 'Beklemede' },
  { value: 1, label: 'Tamamlandi' },
  { value: 2, label: 'Iptal' },
];
const REQUEST_STATUS_OPTIONS = [
  { value: 0, label: 'Yok' },
  { value: 1, label: 'Talep Gonderildi' },
  { value: 2, label: 'Onaylandi' },
  { value: 3, label: 'Reddedildi' },
];

const EMPTY_FORM = {
  textId: '',
  invoiceId: '',
  paymentTypeId: '',
  paymentDate: '',
  amount: '',
  bankName: '',
  receiptNo: '',
  status: '0',
  transactionRequestStatus: '0',
  description: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function toDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toNullableDate(value) {
  return value ? new Date(value).toISOString() : null;
}

function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function statusLabel(value) {
  return STATUS_OPTIONS.find((o) => o.value === toInt(value))?.label || 'Beklemede';
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

function invoiceLabelOf(inv) {
  if (!inv) return '';
  const code = inv.textId || inv.TextId || '';
  const net = inv.netAmount ?? inv.NetAmount;
  return net != null ? `${code} - ${formatMoney(net)}` : code;
}

export function PaymentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Odeme' : 'Odeme Detayi', 'Odeme kaydini fatura ile bagli sekilde duzenleyin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const [paymentTypes, setPaymentTypes] = useState([]); // küçük lookup → tümü /Select
  const [invoiceLabels, setInvoiceLabels] = useState({}); // id -> { label, netAmount, dueDate, textId } (lazy)

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Küçük lookup: ödeme tipleri (tümü)
  useEffect(() => {
    fetch(`${PAYMENT_TYPE_API}/Select`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const items = unwrapLookupItems(data)
          .filter((item) => item?.isActive !== false)
          .sort((a, b) => (a.textId || '').localeCompare(b.textId || '', 'tr'));
        setPaymentTypes(items);
      })
      .catch(() => {});
  }, []);

  const invoiceName = useCallback((iid) => (iid ? (invoiceLabels[iid]?.label || '…') : ''), [invoiceLabels]);

  // Büyük lookup: fatura etiketlerini id bazında lazy çek.
  const invFetchRef = useRef(new Set());
  const ensureInvoiceLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((iid) => iid && invoiceLabels[iid] === undefined && !invFetchRef.current.has(iid)))];
    if (!need.length) return;
    need.forEach((iid) => invFetchRef.current.add(iid));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((iid) => `Id eq ${iid}`).join(' or ');
        const url = `${INVOICE_API}?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) {
          const data = await res.json();
          getODataRows(data).forEach((inv) => {
            found[inv.id] = { label: invoiceLabelOf(inv), netAmount: inv.netAmount ?? 0, dueDate: inv.dueDate ?? null, textId: inv.textId || '' };
          });
        }
        setInvoiceLabels((prev) => { const next = { ...prev }; chunk.forEach((iid) => { next[iid] = found[iid] || { label: '-', netAmount: 0, dueDate: null, textId: '' }; }); return next; });
      }
    } finally {
      need.forEach((iid) => invFetchRef.current.delete(iid));
    }
  }, [invoiceLabels]);

  // Sunucu araması: fatura (sayfalı SearchableSelect)
  const searchInvoices = useCallback(async (q, skip = 0) => {
    const query = (q || '').replace(/'/g, "''").toLowerCase();
    let url = `${INVOICE_API}?$top=${PAGE_SIZE}&$skip=${skip}&$orderby=TextId`;
    if (query) url += `&$filter=${encodeURIComponent(`contains(tolower(TextId),'${query}')`)}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return getODataRows(data).map((inv) => ({ value: inv.id, label: invoiceLabelOf(inv) }));
  }, []);

  const fillFromRow = useCallback((r) => {
    setForm({
      textId: pick(r, 'textId', 'TextId') ?? '',
      invoiceId: pick(r, 'invoiceId', 'InvoiceId') ?? '',
      paymentTypeId: pick(r, 'paymentTypeId', 'PaymentTypeId') ?? '',
      paymentDate: dateInputValue(pick(r, 'paymentDate', 'PaymentDate')),
      amount: pick(r, 'amount', 'Amount') ?? '',
      bankName: pick(r, 'bankName', 'BankName') ?? '',
      receiptNo: pick(r, 'receiptNo', 'ReceiptNo') ?? '',
      status: String(pick(r, 'status', 'Status') ?? 0),
      transactionRequestStatus: String(pick(r, 'transactionRequestStatus', 'TransactionRequestStatus') ?? 0),
      description: pick(r, 'description', 'Description') ?? '',
    });
    // Bağlı fatura özetini ilişkili kayıttan doldur (varsa); yoksa lazy çek.
    const invId = pick(r, 'invoiceId', 'InvoiceId');
    const inv = pick(r, 'invoice', 'Invoice');
    if (invId && inv) {
      setInvoiceLabels((prev) => ({
        ...prev,
        [invId]: {
          label: invoiceLabelOf(inv),
          netAmount: pick(inv, 'netAmount', 'NetAmount') ?? 0,
          dueDate: pick(inv, 'dueDate', 'DueDate') ?? null,
          textId: pick(inv, 'textId', 'TextId') ?? '',
        },
      }));
    } else if (invId) {
      ensureInvoiceLabels([invId]);
    }
  }, [ensureInvoiceLabels]);

  // Tek kayıt yükleme: önce location.state.row ile başlık, sonra GetById ile tam detay.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    const row = location.state?.row;
    if (row) fillFromRow(row);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || `Odeme alinamadi (${res.status})`);
      const r = unwrap(body);
      if (r) {
        fillFromRow(r);
        setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
      }
    } catch (error) {
      showToast('error', error.message || 'Detay alinirken hata olustu');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, fillFromRow]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /payment/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      const c = location.state.clone;
      setForm({ ...EMPTY_FORM, ...c });
      setTried(false);
      const invId = c.invoiceId;
      if (invId && c.invoiceLabel) {
        setInvoiceLabels((prev) => ({ ...prev, [invId]: { label: c.invoiceLabel, netAmount: 0, dueDate: null, textId: '' } }));
        ensureInvoiceLabels([invId]);
      } else if (invId) {
        ensureInvoiceLabels([invId]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    textId: form.textId.trim(),
    invoiceId: form.invoiceId,
    paymentTypeId: form.paymentTypeId,
    paymentDate: toNullableDate(form.paymentDate),
    amount: toDecimal(form.amount),
    bankName: form.bankName.trim(),
    receiptNo: form.receiptNo.trim(),
    status: toInt(form.status),
    transactionRequestStatus: toInt(form.transactionRequestStatus),
    description: form.description.trim(),
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan. Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Odeme referansi bos olamaz'); return; }
    if (!form.invoiceId) { showToast('error', 'Fatura secilmelidir'); return; }
    if (!form.paymentTypeId) { showToast('error', 'Odeme tipi secilmelidir'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || (isNew ? 'Odeme kaydi eklenemedi' : 'Odeme kaydi guncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Odeme kaydi eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Odeme kaydi eklendi');
        setTimeout(() => navigate('/payment'), 400);
        return;
      }
      showToast('success', 'Odeme kaydi guncellendi');
      setTimeout(() => navigate('/payment'), 400);
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/payment/new', {
      state: {
        clone: { ...form, invoiceLabel: form.invoiceId ? invoiceLabels[form.invoiceId]?.label : undefined },
      },
    });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Odeme kaydi silinemedi');
      showToast('success', 'Odeme kaydi silindi');
      setTimeout(() => navigate('/payment'), 400);
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (() => {
    const v = toInt(form.status);
    const tone = v === 1 ? 'ok' : v === 2 ? 'bad' : 'neutral';
    return <StatusBadge tone={tone}>{statusLabel(v)}</StatusBadge>;
  })();

  const selectedInvoice = invoiceLabels[form.invoiceId];

  const paymentTypeOptions = useMemo(
    () => [{ value: '', label: 'Odeme tipi secin' }, ...paymentTypes.map((p) => ({ value: p.id, label: `${p.textId} - ${p.description}` }))],
    [paymentTypes],
  );

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/payment')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni ödeme aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Odeme Takip', to: '/payment' }, { label: isNew ? 'Yeni Odeme' : 'Odeme Detayi', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Payment', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="wallet" title="Odeme Bilgileri" subtitle="Referans, fatura ve ödeme tipi">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Odeme No" req mono />
            <FieldCustom label="Fatura" req invalid={tried && !form.invoiceId}>
              <SearchableSelect
                value={form.invoiceId || ''}
                selectedLabel={form.invoiceId ? invoiceName(form.invoiceId) : ''}
                onChange={(val, item) => {
                  set('invoiceId', val);
                  if (val && item?.label) setInvoiceLabels((prev) => ({ ...prev, [val]: { ...(prev[val] || {}), label: item.label } }));
                  if (val) ensureInvoiceLabels([val]);
                }}
                fetchOptions={searchInvoices}
                placeholder="Fatura ara…"
                pageSize={PAGE_SIZE}
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} tried={tried} k="paymentTypeId" label="Odeme Tipi" req options={paymentTypeOptions} />
            <FieldDate form={form} set={set} k="paymentDate" label="Odeme Tarihi" />
            <FieldText form={form} set={set} k="amount" label="Odeme Tutari" num />
            <FieldText form={form} set={set} k="bankName" label="Banka" />
            <FieldText form={form} set={set} k="receiptNo" label="Makbuz No" />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))} />
            <FieldSelect form={form} set={set} k="transactionRequestStatus" label="Islem Talep Durumu" options={REQUEST_STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))} span />
            <FieldTextarea form={form} set={set} k="description" label="Aciklama" rows={3} span />
          </Grid>
        </Section>

        <Section icon="info" title="Bagli Fatura Ozeti" subtitle="Seçilen faturanın güncel bilgileri">
          <div style={{ color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.8 }}>
            <div><strong>Fatura No:</strong> {selectedInvoice?.textId || invoiceName(form.invoiceId) || '-'}</div>
            <div><strong>Net Tutar:</strong> {formatMoney(selectedInvoice?.netAmount)}</div>
            <div><strong>Vade:</strong> {formatDate(selectedInvoice?.dueDate)}</div>
          </div>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayi"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> odeme kaydini silmek istediginize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default PaymentDetailPage;
